import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Heading from '@tiptap/extension-heading';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import { 
  ArrowLeftIcon,
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon,
  LinkIcon,
  CodeBracketIcon,
  ChatBubbleBottomCenterTextIcon,
  PhotoIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../../../context/ToastContext';
import { useProject } from '../../../context/ProjectContext';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../context/AuthContext';
import { deleteContentImage } from '../../../lib/storage';
import MediaPicker from '../../media/MediaPicker';
import LoadingOverlay from '../../common/LoadingOverlay';
import { CHANNELS } from '../../../lib/channelConfig';
import axios from 'axios';
import { executeBlog } from '../../agents/documentAi';
// Define the GenerateContentModal component
const GenerateContentModal = ({ onClose, onGenerate, loading }) => {
  const [topic, setTopic] = useState('');
  const [wordCount, setWordCount] = useState(1000);
  const [requireImage, setRequireImage] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    onGenerate({ topic, wordCount, requireImage });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-dark-200 rounded-xl p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Generate Content with Clive</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Topic Description */}
          <div>
            <label htmlFor="topic" className="block text-sm font-medium mb-2">
              Topic Description
            </label>
            <textarea
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full px-4 py-2 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
              placeholder="Describe what you want to write about..."
              rows={4}
              required
            />
          </div>

          {/* Word Count */}
          <div>
            <label htmlFor="wordCount" className="block text-sm font-medium mb-2">
              Word Count
            </label>
            <input
              type="number"
              id="wordCount"
              value={wordCount}
              onChange={(e) => setWordCount(parseInt(e.target.value))}
              className="w-full px-4 py-2 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
              min={100}
              max={5000}
              step={100}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add this function after getProjectStrategies and before the main component
const findBestImageForPost = async (postTitle) => {
  try {
    const response = await axios.post('https://aai.bigg.co.uk/webhook/find-image', {
      postTitle
    });

    console.log('Find Image Response:', response);
    
    // Parse the nested JSON string from the response
    const resultContent = JSON.parse(response.data.message.content);

    console.log('Find Image Result Content:', resultContent);

    return resultContent;
  } catch (error) {
    console.error('Error finding best image:', error);
    throw error;
  }
};

// MenuBar component from WordPressContent
const MenuBar = ({ editor }) => {
  const addHeading = React.useCallback((level) => {
    if (!editor) return;
    editor.chain().focus().toggleHeading({ level }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border-b border-divider p-2 flex flex-wrap gap-2">
      {/* Headings */}
      <div className="flex gap-1">
        <button
          onClick={() => addHeading(1)}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('heading', { level: 1 }) ? 'bg-dark-400' : ''}`}
        >
          <span className="font-bold">H1</span>
        </button>
        <button
          onClick={() => addHeading(2)}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('heading', { level: 2 }) ? 'bg-dark-400' : ''}`}
        >
          <span className="font-bold">H2</span>
        </button>
        <button
          onClick={() => addHeading(3)}
          className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('heading', { level: 3 }) ? 'bg-dark-400' : ''}`}
        >
          <span className="font-bold">H3</span>
        </button>
      </div>

      <div className="w-px h-6 bg-divider mx-1 self-center" />

      {/* Text formatting */}
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('bold') ? 'bg-dark-400' : ''}`}
      >
        <BoldIcon className="w-5 h-5" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('italic') ? 'bg-dark-400' : ''}`}
      >
        <ItalicIcon className="w-5 h-5" />
      </button>

      <div className="w-px h-6 bg-divider mx-1 self-center" />

      {/* Lists */}
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('bulletList') ? 'bg-dark-400' : ''}`}
      >
        <ListBulletIcon className="w-5 h-5" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('orderedList') ? 'bg-dark-400' : ''}`}
      >
        <NumberedListIcon className="w-5 h-5" />
      </button>

      <div className="w-px h-6 bg-divider mx-1 self-center" />

      {/* Other formatting */}
      <button
        onClick={() => {
          const url = window.prompt('Enter URL:');
          if (url) {
            editor.chain().focus().setLink({ href: url }).run();
          }
        }}
        className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('link') ? 'bg-dark-400' : ''}`}
      >
        <LinkIcon className="w-5 h-5" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('codeBlock') ? 'bg-dark-400' : ''}`}
      >
        <CodeBracketIcon className="w-5 h-5" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`p-2 rounded hover:bg-dark-400 ${editor.isActive('blockquote') ? 'bg-dark-400' : ''}`}
      >
        <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export default function BlogContent({ isEditing = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { addToast } = useToast();
  const { currentProject } = useProject();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });

  // Move editor configuration here, before any conditional returns
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      Heading.configure({
        levels: [1, 2, 3],
        HTMLAttributes: {
          class: {
            1: 'text-4xl font-bold mb-8 mt-12',
            2: 'text-3xl font-semibold mb-6 mt-10',
            3: 'text-2xl font-medium mb-4 mt-8',
          },
        },
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: 'list-disc ml-6 space-y-2 mb-8',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'list-decimal ml-6 space-y-2 mb-8',
        },
      }),
      ListItem.configure({
        HTMLAttributes: {
          class: 'pl-1.5 mb-1',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-accent-primary hover:underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start writing your content...',
      }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({
        ...prev,
        content: editor.getHTML()
      }));
    },
    editorProps: {
      attributes: {
        class: `prose prose-invert prose-headings:font-display max-w-none focus:outline-none min-h-[500px] px-4
          [&>p]:mb-4 [&>p]:leading-relaxed
          [&>h1]:text-4xl [&>h1]:font-bold [&>h1]:mb-8 [&>h1]:mt-12
          [&>h2]:text-3xl [&>h2]:font-semibold [&>h2]:mb-6 [&>h2]:mt-10
          [&>h3]:text-2xl [&>h3]:font-medium [&>h3]:mb-4 [&>h3]:mt-8
          [&>blockquote]:border-l-4 [&>blockquote]:border-accent-primary/50 [&>blockquote]:pl-4 
          [&>blockquote]:italic [&>blockquote]:my-6 [&>blockquote]:text-gray-400
          [&>pre]:bg-dark-400 [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:my-4
          [&>ul]:mb-8 [&>ol]:mb-8 [&_li]:mb-1 [&_li:last-child]:mb-0`.replace(/\s+/g, ' ').trim(),
      },
    },
  });

  // Add useEffect to fetch content data
  useEffect(() => {
    const fetchContent = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const { data: contentData, error } = await supabase
          .from('content')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;

        setFormData({
          title: contentData.title || '',
          content: contentData.content || ''
        });
        
        setImages(contentData.images || []);

        // Update editor content if available
        if (editor && contentData.content) {
          editor.commands.setContent(contentData.content);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
        addToast('Failed to load content', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [id, editor]);

  // Loading spinner can come after hooks
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
      </div>
    );
  }

  // Update handleSubmit to use id
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (showMediaPicker) return;
    setLoading(true);

    try {
      const contentData = {
        project_id: currentProject.id,
        title: formData.title,
        content: formData.content,
        node_type: 'blog',
        updated_at: new Date().toISOString(),
        updated_by: user.id,
        images: images
      };

      const { error } = await supabase
        .from('content')
        .update(contentData)
        .eq('id', id);

      if (error) throw error;

      addToast('Blog post saved successfully', 'success');
      
    } catch (error) {
      console.error('Error saving content:', error);
      addToast('Failed to save content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWithAI = async ({ topic, wordCount, requireImage }) => {
    try {
      setLoading(true);
      setLoadingMessage('Generating content...');
      setShowGenerateModal(false);

      const result = await executeBlog({
        userPrompt: topic,
        wordCount,
        projectId: currentProject.id,
        organizationId: currentProject.organization_id,
        userId: user.id
      });

      if (result.success && result.output) {
        // Update form data with generated content
        setFormData(prev => ({
          ...prev,
          title: result.output.title || prev.title,
          content: result.output.content || prev.content
        }));

        // Update editor content
        if (editor && result.output.content) {
          editor.commands.setContent(result.output.content);
        }

        // If image generation was requested, find best image based on title
        if (requireImage && result.output.title) {
          setLoadingMessage('Finding the perfect image...');
          const imageResult = await findBestImageForPost(result.output.title);

          console.log('Find Image Result:', imageResult);

          if (imageResult && imageResult.image_url) {
            setImages([imageResult.image_url]);
          }
        }

        addToast('Content generated successfully', 'success');
      } else {
        throw new Error('Failed to generate content');
      }
      
      setLoading(false);
      setLoadingMessage('');
      
    } catch (error) {
      console.error('Error generating content:', error);
      addToast('Failed to generate content', 'error');
      setLoading(false);
      setLoadingMessage('');
    }
  };

  

  const handleRemoveImage = async (index) => {
    try {
      const image = images[index];
      if (image.includes('storage.googleapis.com')) {
        const path = new URL(image).pathname.split('/').pop();
        await deleteContentImage(path);
      }
      setImages(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error removing image:', error);
      addToast('Failed to remove image', 'error');
    }
  };

  // Use the node name from the config
  const nodeName = 'Content';

  return (
    <div className="py-6">
      {loading && loadingMessage && <LoadingOverlay message={loadingMessage} />}
      
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-semibold">
              {isEditing ? `Edit ${nodeName}` : `Create ${nodeName}`}
            </h1>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Post'}
            </button>
            <button
              onClick={() => setShowGenerateModal(true)}
              className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors"
            >
              Generate with Clive
            </button>
          </div>
        </div>

        {/* Content Editor and Featured Image Layout */}
        <div className="grid grid-cols-3 gap-6">
          {/* Content Editor - Takes up 2/3 of space */}
          <div className="col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium mb-2">
                  Title
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
                  placeholder="Enter title"
                  required
                />
              </div>

              {/* Content Editor */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Content
                </label>
                <div className="border border-divider rounded-lg overflow-hidden">
                  <MenuBar editor={editor} />
                  <div className="p-6">
                    <EditorContent editor={editor} />
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Featured Image Section - Takes up 1/3 of space */}
          <div>
            <div className="sticky top-6 space-y-6">
              <div className="bg-dark-200 rounded-xl p-6 border border-divider">
                <h2 className="text-lg font-medium mb-6">Featured Image</h2>
                <div 
                  className="border border-divider rounded-lg p-4 cursor-pointer"
                  onClick={() => setShowMediaPicker(true)}
                >
                  {images && images.length > 0 ? (
                    <div className="relative group">
                      <img
                        src={images[0]}
                        alt="Featured"
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(0);
                          }}
                          className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                        >
                          <XMarkIcon className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-video flex items-center justify-center">
                      <div className="text-center">
                        <PhotoIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">Click to add featured image</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Image Guidelines */}
                <div className="mt-4 p-4 bg-dark-300 rounded-lg">
                  <h4 className="font-medium mb-2">Image Guidelines</h4>
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li>• Recommended size: 1200 x 630 pixels</li>
                    <li>• Maximum file size: 5MB</li>
                    <li>• Supported formats: JPG, PNG</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showGenerateModal && (
        <GenerateContentModal
          onClose={() => setShowGenerateModal(false)}
          onGenerate={handleGenerateWithAI}
          loading={loading}
        />
      )}

      {showMediaPicker && (
        <MediaPicker
          isOpen={showMediaPicker}
          onSelect={(media) => {
            setImages([media.url]);
            setShowMediaPicker(false);
          }}
          onClose={() => setShowMediaPicker(false)}
        />
      )}
    </div>
  );
} 